/**
 * WhatsAppMiniInbox — Iter Feb 2026 R44.
 *
 * Compact 2-pane inbox that reuses the same `/wa/threads` +
 * `/wa/threads/:ref/messages` APIs as the main WhatsApp Inbox but in
 * a much smaller footprint. Used inside:
 *   • The floating WA widget (FloatingWaWidget)
 *   • The per-lead chat popup
 *
 * Props:
 *   preselectRef?: string       — auto-open this contact_ref
 *   preselectPhone?: string     — fallback: find thread by phone
 *   preselectName?: string      — display before thread loads
 *   preselectLeadId?: string    — Feb 2026 fix · Enables "Send Template"
 *                                  inline when NO prior conversation
 *                                  exists on the lead. Builds a
 *                                  `lead:<id>` contact_ref so the
 *                                  template send call reaches the
 *                                  right CRM record even before any
 *                                  wa_messages row has been created.
 *   onClose?: () => void
 *   showThreadList?: boolean (default true)
 *   height?: string (default '520px')
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Send, Search, MessageCircle, Clock, Circle, RefreshCw, FileText, Loader2 } from "lucide-react";
import { pollWhenVisible } from "@/lib/pollWhenVisible";

const _last10 = (p) => String(p || "").replace(/\D/g, "").slice(-10);

/* Feb 2026 · Same window-remaining formatter used in the main WaInbox,
   kept duplicated here (not re-exported) so the mini inbox has zero
   cross-file deps for its lazy-import path. */
function _fmtWindowRemaining(expiresIso, { compact = false } = {}) {
  if (!expiresIso) return compact ? "—" : "awaiting reply";
  const t = new Date(expiresIso).getTime();
  if (isNaN(t)) return compact ? "—" : "awaiting reply";
  const diffMs = t - Date.now();
  if (diffMs <= 0) return compact ? "expired" : "expired · needs template";
  const mins = Math.floor(diffMs / 60000);
  const hrs = Math.floor(mins / 60);
  const remM = mins % 60;
  if (hrs >= 1) return compact ? `${hrs}h${remM ? `${remM}m` : ""}` : `in ${hrs}h ${remM}m`;
  return compact ? `${mins}m` : `in ${mins}m`;
}

export default function WhatsAppMiniInbox({
  preselectRef, preselectPhone, preselectName, preselectLeadId,
  showThreadList = true,
  height = "520px", onClose,
}) {
  const { activeWorkspaceId } = useAuth();
  const [threads, setThreads] = useState([]);
  const [q, setQ] = useState("");
  const [selectedRef, setSelectedRef] = useState(preselectRef || null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [window24, setWindow24] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  // Feb 2026 fix · Inline template picker for the "no conversation yet"
  // empty state. Lets Edit-Lead → WhatsApp open a fresh conversation
  // with any Interakt-approved template without bouncing to the main
  // WA Inbox.
  const [tplDialogOpen, setTplDialogOpen] = useState(false);
  const bottomRef = useRef(null);

  const loadThreads = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/wa/threads?limit=100`);
      const rows = data?.rows || data || [];
      setThreads(rows);
      // Preselect by phone if no ref
      if (!selectedRef && preselectPhone && !preselectRef) {
        const p10 = _last10(preselectPhone);
        const match = rows.find(t => _last10(t.phone) === p10);
        if (match) setSelectedRef(match.contact_ref);
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load threads")); }
    finally { setLoading(false); }
  }, [activeWorkspaceId, selectedRef, preselectPhone, preselectRef]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  // Poll threads for realtime feel. Feb 2026 R281 · Bumped 20s → 30s
  // and visibility-gated so background tabs stop hitting.
  useEffect(() => {
    return pollWhenVisible(loadThreads, 30000);
  }, [loadThreads]);

  const loadMessages = useCallback(async () => {
    if (!activeWorkspaceId || !selectedRef) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/wa/threads/${encodeURIComponent(selectedRef)}/messages?limit=100`);
      setMessages(data?.rows || data?.messages || []);
      const { data: w } = await api.get(`/workspaces/${activeWorkspaceId}/wa/threads/${encodeURIComponent(selectedRef)}/window`);
      setWindow24(w);
      // Mark thread read
      const prevUnread = (threads.find((t) => t.contact_ref === selectedRef) || {}).unread_count || 0;
      api.post(`/workspaces/${activeWorkspaceId}/wa/threads/${encodeURIComponent(selectedRef)}/mark-read`)
         .then(() => {
           // R51 · Optimistic per-thread + header badge decrement.
           setThreads((prev) => prev.map((t) =>
             t.contact_ref === selectedRef && (t.unread_count || 0) > 0
               ? { ...t, unread_count: 0 }
               : t,
           ));
           window.dispatchEvent(new CustomEvent("acq:wa-unread-refresh"));
           window.dispatchEvent(new CustomEvent("acq:wa-thread-read",
             { detail: { contact_ref: selectedRef, prev_unread: prevUnread } }));
         })
         .catch(() => {});
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load messages")); }
  }, [activeWorkspaceId, selectedRef]);

  useEffect(() => { loadMessages(); }, [loadMessages]);
  useEffect(() => {
    if (!selectedRef) return;
    // Feb 2026 R281 · Bumped 8s → 15s + visibility-gated. Only ticks
    // while the user is actively looking at the mini-inbox.
    return pollWhenVisible(loadMessages, 15000);
  }, [selectedRef, loadMessages]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const filtered = useMemo(() => {
    if (!q.trim()) return threads;
    const s = q.trim().toLowerCase();
    return threads.filter(t =>
      (t.name || "").toLowerCase().includes(s) ||
      (t.phone || "").includes(s) ||
      _last10(t.phone).includes(s));
  }, [threads, q]);

  const currentThread = threads.find(t => t.contact_ref === selectedRef);

  const send = async () => {
    if (!text.trim() || !selectedRef) return;
    if (window24 && !window24.open) {
      return toast.error("24h Meta window is closed. Send a template first.");
    }
    setSendBusy(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/wa/threads/${encodeURIComponent(selectedRef)}/messages`, {
        text: text.trim(),
      });
      setText("");
      loadMessages();
    } catch (e) { toast.error(apiErrorMessage(e, "Send failed")); }
    finally { setSendBusy(false); }
  };

  return (
    <div className="flex bg-background border" style={{ height }} data-testid="wa-mini-inbox">
      {/* Thread list */}
      {showThreadList && (
        <div className="w-56 border-r flex flex-col shrink-0">
          <div className="p-2 border-b flex items-center gap-1.5">
            <Search className="w-3.5 h-3.5 text-muted-foreground" />
            <Input value={q} onChange={e => setQ(e.target.value)}
                     placeholder="Search…" className="h-7 rounded-none text-xs border-0 px-0"
                     data-testid="wami-search" />
          </div>
          <div className="flex-1 overflow-auto">
            {loading && filtered.length === 0 && (
              <div className="p-3 text-xs text-muted-foreground">Loading…</div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="p-3 text-xs text-muted-foreground text-center">
                {preselectPhone ? "No conversation yet — send a template to start." : "No threads."}
              </div>
            )}
            {filtered.map(t => (
              <button key={t.contact_ref}
                onClick={() => setSelectedRef(t.contact_ref)}
                className={`w-full text-left px-2 py-1.5 border-b hover:bg-neutral-50 ${selectedRef === t.contact_ref ? "bg-neutral-100" : ""}`}
                data-testid={`wami-thread-${t.contact_ref}`}>
                <div className="flex items-center gap-1.5">
                  <div className="font-semibold text-xs truncate flex-1">{t.name || t.phone}</div>
                  {t.unread_count > 0 && (
                    <span className="text-[9px] px-1.5 bg-emerald-500 text-white rounded-full font-semibold" data-testid={`wami-unread-${t.contact_ref}`}>{t.unread_count}</span>
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {t.last_direction === "in" ? "← " : "→ "}
                  {(t.last_text || "").slice(0, 40)}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 flex flex-col min-w-0">
        {!selectedRef && (
          <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground p-4 text-center flex-col gap-3">
            <MessageCircle className="w-6 h-6 text-neutral-400" />
            {preselectName && preselectPhone ? (
              <>
                <div>
                  No conversation yet with <b>{preselectName}</b> ({preselectPhone}).
                </div>
                {preselectLeadId ? (
                  <Button size="sm" className="rounded-none h-8"
                           onClick={() => setTplDialogOpen(true)}
                           data-testid="wami-start-template-btn">
                    <FileText className="w-3.5 h-3.5 mr-1" /> Send a template to start
                  </Button>
                ) : (
                  <div className="text-[11px]">
                    Send a template from the main WA Inbox to start.
                  </div>
                )}
              </>
            ) : (
              "Pick a conversation from the left."
            )}
          </div>
        )}
        {selectedRef && (
          <>
            <div className="border-b px-2 py-1.5 flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-xs truncate">{currentThread?.name || preselectName || "—"}</div>
                <div className="text-[10px] text-muted-foreground">{currentThread?.phone || preselectPhone}</div>
              </div>
              {window24 && (
                <span className={`text-[9px] px-1.5 py-0.5 uppercase tracking-widest border ${window24.open ? "border-emerald-400 text-emerald-800 bg-emerald-50" : "border-amber-400 text-amber-800 bg-amber-50"}`}
                        title={window24.open
                          ? (window24.expires_at
                              ? `24h session window closes at ${new Date(window24.expires_at).toLocaleString()} (${_fmtWindowRemaining(window24.expires_at)}).`
                              : "The customer replied within the last 24h — free-text replies are open.")
                          : "The 24h WhatsApp session window re-opens only when the customer replies. Send a template to re-engage."}
                        data-testid="wami-window-badge">
                  {window24.open
                    ? `24h open · closes ${_fmtWindowRemaining(window24.expires_at, { compact: true })}`
                    : "closed · needs template"}
                </span>
              )}
              <Button size="sm" variant="ghost" className="h-6 rounded-none px-1.5" onClick={loadMessages} title="Refresh">
                <RefreshCw className="w-3 h-3" />
              </Button>
            </div>
            <div className="flex-1 overflow-auto p-2 space-y-1 bg-neutral-50" data-testid="wami-messages">
              {messages.map(m => (
                <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] px-2 py-1 text-xs ${m.direction === "out" ? "bg-emerald-100" : "bg-white border"}`}>
                    <div className="whitespace-pre-wrap break-words">{m.text || m.body || <em>(media)</em>}</div>
                    <div className="text-[9px] text-muted-foreground text-right mt-0.5">
                      {(m.created_at || "").slice(11, 16)}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            <div className="border-t p-2 flex items-end gap-1.5">
              <Textarea rows={1} value={text} onChange={e => setText(e.target.value)}
                         onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }}}
                         placeholder={window24?.open ? "Type a reply…" : preselectLeadId ? "24h closed — send a template to re-engage" : "24h closed — use main WA Inbox to send a template"}
                         className="rounded-none text-xs resize-none min-h-8 max-h-24"
                         disabled={window24 && !window24.open}
                         data-testid="wami-input" />
              {window24 && !window24.open && preselectLeadId ? (
                <Button size="sm" className="rounded-none h-8 shrink-0"
                          onClick={() => setTplDialogOpen(true)}
                          title="Send an Interakt-approved template to re-open the 24h window"
                          data-testid="wami-send-template-btn">
                  <FileText className="w-3.5 h-3.5 mr-1" /> Template
                </Button>
              ) : (
                <Button size="sm" className="rounded-none h-8 shrink-0"
                          onClick={send} disabled={sendBusy || !text.trim() || (window24 && !window24.open)}
                          data-testid="wami-send">
                  <Send className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </>
        )}
      </div>
      {tplDialogOpen && preselectLeadId && (
        <InlineTemplateDialog
          wsId={activeWorkspaceId}
          leadId={preselectLeadId}
          leadName={preselectName}
          leadPhone={preselectPhone}
          onClose={() => setTplDialogOpen(false)}
          onSent={() => {
            setTplDialogOpen(false);
            // After a template send the thread exists; refresh and
            // open it so the user sees the outbound bubble immediately.
            loadThreads().then(() => {
              const p10 = _last10(preselectPhone);
              setThreads((prev) => {
                const match = prev.find((t) => _last10(t.phone) === p10);
                if (match) setSelectedRef(match.contact_ref);
                return prev;
              });
            });
          }}
        />
      )}
    </div>
  );
}

/* ───────── Inline template picker ────────
   Minimal dialog for Edit-Lead → WhatsApp → "Send Template" flow when
   NO conversation exists yet. Fetches Interakt templates for the
   workspace, lets the user pick a name, maps body variables, and
   calls `/wa/threads/lead:<id>/send-template` so a brand-new thread
   is created with the correct CRM linkage. */

function InlineTemplateDialog({ wsId, leadId, leadName, leadPhone, onClose, onSent }) {
  const [tpls, setTpls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tplName, setTplName] = useState("");
  const [vars, setVars] = useState({});
  const [sending, setSending] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(
          `/workspaces/${wsId}/integrations/interakt/templates`);
        const list = data?.templates || data?.rows || data || [];
        setTpls(Array.isArray(list) ? list : []);
      } catch (e) {
        toast.error(apiErrorMessage(e, "Could not load templates"));
      } finally { setLoading(false); }
    })();
  }, [wsId]);
  const activeTpl = useMemo(() => {
    const n = tplName.trim().toLowerCase();
    return tpls.find((t) =>
      (t.interakt_template_name || t.name || "").trim().toLowerCase() === n);
  }, [tpls, tplName]);
  const varCount = (activeTpl?.variables || []).length;
  const send = async () => {
    const name = (activeTpl?.interakt_template_name || tplName || "").trim();
    if (!name) {
      toast.error("Pick a template to send");
      return;
    }
    setSending(true);
    try {
      const bodyVars = Array.from({ length: varCount }, (_, i) =>
        (vars[i] || "").trim()).filter(Boolean);
      const r = await api.post(
        `/workspaces/${wsId}/wa/threads/${encodeURIComponent(`lead:${leadId}`)}/send-template`,
        { template_name: name, body_vars: bodyVars });
      const status = r.data?.provider?.status;
      if (status === "sent") {
        toast.success(`Template "${name}" sent to ${leadName || leadPhone}`);
        onSent?.();
      } else {
        const prov = r.data?.provider || {};
        const bits = [prov.reason, prov.provider_status && `HTTP ${prov.provider_status}`, prov.detail]
          .filter(Boolean);
        toast.error(bits.length
          ? `Template send failed — ${bits.join(" · ")}`
          : "Template send failed — check the template name in Interakt",
          { duration: 10000 });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 422 && detail?.code === "missing_country_code") {
        toast.error(detail.message || "Missing country code on this lead's phone — fix it in Edit Lead first.", { duration: 8000 });
      } else {
        toast.error(apiErrorMessage(e, "Template send failed"));
      }
    } finally { setSending(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !sending && !v && onClose?.()}>
      <DialogContent className="max-w-md rounded-none" data-testid="wami-template-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2">
            <FileText className="w-4 h-4 text-emerald-600" />
            Send a WhatsApp template
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-xs">
          <div className="text-muted-foreground">
            Starting a new conversation with <b>{leadName || leadPhone}</b> ({leadPhone}).
            WhatsApp requires an approved template for the first message.
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest mb-1 inline-block">Template</Label>
            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading templates…
              </div>
            ) : tpls.length === 0 ? (
              <div className="text-amber-700 text-[11px]">
                No approved Interakt templates found. Create one in the Interakt console or sync in the main WA Inbox.
              </div>
            ) : (
              <select value={tplName}
                       onChange={(e) => { setTplName(e.target.value); setVars({}); }}
                       className="w-full h-8 border border-border rounded-none text-xs px-2"
                       data-testid="wami-template-select">
                <option value="">— pick a template —</option>
                {tpls.map((t) => {
                  const n = t.interakt_template_name || t.name;
                  return <option key={n} value={n}>{t.name || n}{t.language ? ` · ${t.language}` : ""}</option>;
                })}
              </select>
            )}
          </div>
          {activeTpl?.body && (
            <div className="border border-dashed border-border bg-muted/30 p-2 text-[11px] whitespace-pre-wrap">
              {activeTpl.body}
            </div>
          )}
          {varCount > 0 && (
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-widest">Variables</Label>
              {Array.from({ length: varCount }, (_, i) => (
                <div key={i}>
                  <Label className="text-[10px] text-muted-foreground">
                    {`{{${i + 1}}}`}
                    {(activeTpl?.variables?.[i]?.label) && <span className="ml-1 normal-case">· {activeTpl.variables[i].label}</span>}
                  </Label>
                  <Input value={vars[i] || ""}
                          onChange={(e) => setVars((v) => ({ ...v, [i]: e.target.value }))}
                          placeholder={`Value for {{${i + 1}}}`}
                          className="h-8 rounded-none text-xs"
                          data-testid={`wami-template-var-${i}`} />
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button className="rounded-none" onClick={send} disabled={sending || loading || !tplName}
                   data-testid="wami-template-send">
            {sending ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Sending…</> : <><Send className="w-3.5 h-3.5 mr-1" /> Send template</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
